import React, { useState, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { Typography, Box, Stack, IconButton, Menu, MenuItem, CircularProgress } from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { addGalleryVideo, getGalleryVideos, checkAdminApi, deleteGalleries, getUserById, checkTrainerApi } from '../../utils/api';
import { Buffer } from 'buffer';

if (typeof window !== 'undefined') {
    window.Buffer = Buffer;
}

const Gallery = ({ userId }) => {
    const [mediaItems, setMediaItems] = useState([]);
    const [isAdmin, setIsAdmin] = useState(false);
    const [isTrainer, setIsTrainer] = useState(false);
    const [contextMenu, setContextMenu] = useState(null); // { mouseX, mouseY, itemId } or null

    const [uploadProgress, setUploadProgress] = useState(0); // New state for progress
    const [isUploading, setIsUploading] = useState(false);   // New state for upload status

    const fetchMediaItems = async () => {
        try {
            const adminStatus = await checkAdminApi(userId);
            setIsAdmin(adminStatus);
            const trainerStatus = await checkTrainerApi(userId);
            setIsTrainer(trainerStatus);
            const user = await getUserById(userId);
            if (!user) return;

            let trainerId = user.role === "trainer" ? user._id : user.trainer_id;
            const response = await getGalleryVideos(trainerId, 'gallery'); // Filter by 'gallery'
            const parsedItems = response.videos.map((item) => ({
                url: item.fileType.startsWith('video')
                    ? `https://pilatesnflow.onrender.com/galleries/stream/${item.fileId}`//`http://localhost:5000/galleries/stream/${item.fileId}`// 
                    : `data:${item.fileType};base64,${Buffer.from(item.fileBlob.data).toString('base64')}`,
                id: item._id,
                fileType: item.fileType,
            }));
            setMediaItems(parsedItems);
        } catch (error) {
            console.error('Error fetching media items:', error);
        }
    };


    useEffect(() => {
        fetchMediaItems();
    }, []);

    // Handle file upload (works for both images and videos)
    const onDrop = async (acceptedFiles) => {
        setIsUploading(true); // Indicate upload has started
        const formData = new FormData();
        formData.append('userId', userId);
        // Our server is using the field name 'videoFile' regardless of type.
        formData.append('videoFile', acceptedFiles[0]);
        formData.append('mediaType', 'gallery'); // Add mediaType
        try {
            // await addGalleryVideo(formData);
            await addGalleryVideo(formData, (progress) => {
                setUploadProgress(progress); // Update progress state
            });
            setUploadProgress(0); // Reset progress on success
            fetchMediaItems();
        } catch (error) {
            console.error('Error uploading file:', error);
            setUploadProgress(0); // Reset progress on error
        } finally {
            setIsUploading(false); // Reset uploading status
        }
    };

    const { getRootProps: getMediaRootProps, getInputProps: getMediaInputProps } = useDropzone({
        onDrop,
        accept: 'image/*, video/*',
        disabled: isUploading, // Disable dropzone during upload
        multiple: false,       // Allow only one file at a time
    });

    // Admin context menu to delete an item
    const handleContextMenu = (event, itemId) => {
        if (!isAdmin || !isTrainer) return; // only admin users see the menu
        event.preventDefault();
        setContextMenu({
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
            itemId,
        });
    };

    const handleCloseContextMenu = () => {
        setContextMenu(null);
    };

    const handleDelete = async () => {
        if (contextMenu?.itemId) {
            try {
                await deleteGalleries(contextMenu.itemId);
                fetchMediaItems();
            } catch (error) {
                console.error('Error deleting gallery item:', error);
            }
            handleCloseContextMenu();
        }
    };

    // Render gallery items (both videos and images)
    const renderMediaItems = () => (
        <Stack
            direction="row"
            flexWrap="wrap"
            gap="20px"
            justifyContent="center"
            alignItems="center"
            sx={{ marginBottom: '40px', width: '100%' }}
        >
            {mediaItems.map((item, index) => (
                <Box
                    key={index}
                    sx={{ maxWidth: { lg: '320px', xs: '100%' }, position: 'relative' }}
                    onContextMenu={(e) => handleContextMenu(e, item.id)}
                >
                    {item.fileType.startsWith('video') ? (
                        <video
                            src={item.url}
                            controls
                            style={{ width: '100%', borderRadius: '20px' }}
                        />
                    ) : (
                        <img
                            src={item.url}
                            alt="Gallery"
                            style={{ width: '100%', borderRadius: '20px' }}
                        />
                    )}
                </Box>
            ))}
        </Stack>
    );

    // Render the title with an upload icon for admin users.
    const renderTitleWithIcon = () => (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                marginBottom: '20px',
                gap: '10px',
                flexWrap: { xs: 'wrap', lg: 'nowrap' },
            }}
        >
            <Typography
                sx={{
                    fontFamily: 'GveretLevin',
                    fontSize: { lg: '32px', xs: '20px' },
                    fontWeight: 600,
                    color: '#B5A78A',
                }}
            >
                גלריה
            </Typography>

            {(isAdmin || isTrainer) && (
                <Stack direction="row" alignItems="center" spacing={1}>
                    <IconButton
                        {...getMediaRootProps()}
                        sx={{ color: '#B5A78A', fontSize: '32px' }}
                        disabled={isUploading}
                    >
                        <AddCircleIcon fontSize="large" />
                        <input {...getMediaInputProps()} />
                    </IconButton>
                    {isUploading && (
                        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                            <CircularProgress variant="determinate" value={uploadProgress} />
                            <Box
                                sx={{
                                    top: 0,
                                    left: 0,
                                    bottom: 0,
                                    right: 0,
                                    position: 'absolute',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <Typography variant="caption" component="div" color="text.secondary">
                                    {`${uploadProgress}%`}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Stack>
            )}
        </Box>
    );

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: '100vh',
                backgroundColor: '#FFFAFB',
                padding: '20px',
            }}
        >
            {renderTitleWithIcon()}
            {renderMediaItems()}
            {/* Context Menu for deleting an item (only for admins) */}
            <Menu
                open={contextMenu !== null}
                onClose={handleCloseContextMenu}
                anchorReference="anchorPosition"
                anchorPosition={
                    contextMenu !== null
                        ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                        : undefined
                }
            >
                <MenuItem onClick={handleDelete}>Delete</MenuItem>
            </Menu>
        </Box>
    );
};

export default Gallery;
